export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * CSR-1140 — Strip a phone string down to raw digits for storage, capped at the
 * 10 digits of a US phone number. The canonical stored form is digits-only.
 */
export function unformatPhone(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

/**
 * CSR-1140 — Format a stored phone value for display (and for the masked input
 * `value`). Masks 0–10 digit values via {@link formatPhone}, but leaves
 * malformed legacy values (>10 digits, e.g. a mistyped `70412345678888888`)
 * untouched rather than silently truncating them.
 */
export function formatPhoneDisplay(value: string | null | undefined): string {
  const digits = (value ?? "").replace(/\D/g, "");
  if (digits.length === 0) return "";
  if (digits.length > 10) return value as string;
  return formatPhone(digits);
}

export function formatDollar(value: string | number): string {
  const digits = String(value).replace(/[^\d.]/g, "");
  if (!digits) return "";
  const parts = digits.split(".");
  const whole = parts[0].slice(0, 10);
  const formatted = Number(whole).toLocaleString("en-US");
  if (parts.length > 1) {
    return `$${formatted}.${parts[1].slice(0, 2)}`;
  }
  return `$${formatted}`;
}

export function unformatDollar(value: string | number): string {
  return String(value).replace(/[^\d.]/g, "").slice(0, 15);
}

export function formatZip(value: string): string {
  return value.replace(/\D/g, "").slice(0, 5);
}

export function formatName(value: string): string {
  if (!value) return "";
  return value
    .slice(0, 50)
    .split(/(\s+)/)
    .map((part) =>
      /\s/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");
}

export function formatCompanyName(value: string): string {
  if (!value) return "";
  const upperTokens = ["LLC", "INC", "CO", "CORP", "LTD", "LP", "DBA"];
  return value
    .slice(0, 200)
    .split(/(\s+)/)
    .map((part) => {
      if (/\s/.test(part)) return part;
      if (upperTokens.includes(part.toUpperCase())) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join("");
}

export function formatEmail(value: string): string {
  return value.toLowerCase().slice(0, 200);
}

export function formatLicenseNumber(value: string): string {
  return value.toUpperCase().slice(0, 30);
}

export function formatPolicyNumber(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 30);
}

export function formatDateInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  let mm = digits.slice(0, 2);
  if (mm.length === 2) {
    const m = parseInt(mm);
    if (m === 0) mm = "01";
    else if (m > 12) mm = "12";
  }
  if (digits.length <= 2) return mm;
  let dd = digits.slice(2, 4);
  if (dd.length === 2) {
    const d = parseInt(dd);
    const m = parseInt(mm);
    const maxDay = m === 2 ? 29 : [4, 6, 9, 11].includes(m) ? 30 : 31;
    if (d === 0) dd = "01";
    else if (d > maxDay) dd = String(maxDay);
  }
  if (digits.length <= 4) return `${mm}/${dd}`;
  return `${mm}/${dd}/${digits.slice(4, 8)}`;
}

export function parseDateFilter(display: string): string | undefined {
  if (display.length !== 10) return undefined;
  const match = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return undefined;
  const [, mm, dd, yyyy] = match;
  const date = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(date.getTime())) return undefined;
  if (date.getMonth() + 1 !== parseInt(mm) || date.getDate() !== parseInt(dd)) return undefined;
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * CSR-1634 — Extract the leading calendar date from a date-only string or an
 * ISO date/timestamp, discarding any time/offset component. Driver DOBs arrive
 * both as `yyyy-mm-dd` and as ISO timestamps like `1979-05-03T00:00:00-05:00`;
 * date-only fields (DOB, hire date) must ignore the time/offset so they never
 * shift across the viewer's timezone. Returns null for non-ISO input.
 */
function isoDatePart(value: string): string | null {
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

/**
 * CSR-1391 — Normalize a stored date to ISO `yyyy-mm-dd` for a native
 * `<input type="date">`, which only accepts ISO. Accepts an already-ISO value
 * or `MM/DD/YYYY` (the format GEICO-synced driver DOBs arrive in). Returns ""
 * for empty or unparseable input so the input renders blank instead of binding
 * a value the control silently rejects (which previously wiped the DOB on save).
 * CSR-1634 — Also accepts an ISO timestamp (e.g. `1979-05-03T00:00:00-05:00`),
 * returning just its `yyyy-mm-dd` date part so the edit modal seeds the real DOB
 * instead of defaulting to today.
 */
export function toIsoDate(value: string): string {
  if (!value) return "";
  const isoPart = isoDatePart(value);
  if (isoPart) return isoPart;
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return "";
  const [, month, day, year] = match;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

/**
 * CSR-1391 — Convert an ISO `yyyy-mm-dd` value (as emitted by an
 * `<input type="date">`) back to the canonical `MM/DD/YYYY` storage format.
 * Non-ISO input is returned unchanged so an untouched value round-trips
 * byte-for-byte.
 */
export function fromIsoDate(value: string): string {
  if (!value) return "";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

/**
 * Format a date string as "MM/DD/YYYY" (date only).
 *
 * CSR-1378: GEICO-synced driver DOBs arrive as "MM/DD/YYYY" — normalize to
 * ISO before the length-10 local-midnight branch, which otherwise produces
 * an Invalid Date ("06/02/1974T00:00:00") and renders "—".
 *
 * CSR-1634: driver DOB / hire-date fields are stored as date-only values that
 * may arrive as an ISO timestamp with an embedded offset (e.g.
 * "1979-05-03T00:00:00-05:00"). Pass `{ dateOnly: true }` for those so the
 * leading yyyy-mm-dd is taken and the value flows through the local-midnight
 * branch instead of `new Date(timestamp)`, which converts through the embedded
 * offset + viewer TZ and can render an off-by-one date. The default (no opts)
 * preserves the original timestamp handling so true timestamps
 * (createdAt / lastSentAt / receivedAt …) still render at the viewer's LOCAL
 * calendar date.
 */
export function formatDate(
  dateStr: string | null | undefined,
  opts?: { dateOnly?: boolean },
): string {
  const d = toLocalDate(dateStr, opts);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

// Shared MM/DD/YYYY + ISO normalization behind formatDate and formatLongDate —
// one source so the two display formats can never drift on parsing.
function toLocalDate(
  dateStr: string | null | undefined,
  opts?: { dateOnly?: boolean },
): Date | null {
  if (!dateStr) return null;
  const mdy = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  const normalized = mdy
    ? `${mdy[3]}-${mdy[1]}-${mdy[2]}`
    : opts?.dateOnly
      ? (isoDatePart(dateStr) ?? dateStr)
      : dateStr;
  const d = new Date(normalized.length === 10 ? normalized + "T00:00:00" : normalized);
  return isNaN(d.getTime()) ? null : d;
}

// CSR-1844 — Event Logging Spec long date ("May 16, 2019") for the Activity
// Log page. Same normalization as formatDate; "—" for unparseable input.
export function formatLongDate(dateStr: string): string {
  const d = toLocalDate(dateStr);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** Format a date string as "MM/DD/YYYY, h:mm AM/PM" (date + time) */
export function formatDateTime(dateStr: string): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "2-digit", day: "2-digit", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

/**
 * CSR-771 — Format a date as "MMM D, h:mm A" in the viewer's local timezone.
 * Used for note rendering on the Activity Timeline. Distinct from
 * `formatDateTime` (which uses MM/DD/YYYY) so other usages of that formatter
 * are unaffected.
 */
export function formatNoteDateTime(dateStr: string | Date): string {
  const d = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// CSR-753 — "MMM D, YYYY, h:mm A" in viewer's local timezone (e.g. "Apr 28, 2026, 11:22 AM").
export function formatLastUpdated(dateStr: string | Date | null | undefined): string {
  if (!dateStr) return "—";
  const d = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
